import ThemedSelect from "../components/shared/ThemedSelect.jsx";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useNavigationType, useSearchParams } from "react-router-dom";
import { Button, Dropdown, Form } from "react-bootstrap";
import {
  getHomeStats,
  listClaimStatuses,
  listEntityRows,
  listRuleOptions,
  listUsers,
  searchClaims,
} from "../services/apiClient.js";
import { HomeStatsInline } from "../components/home/HomeBand.jsx";
import RdrChips from "../components/home/RdrChips.jsx";
import RecentCompletions from "../components/home/RecentCompletions.jsx";
import JobList, { DEFAULT_COLUMNS } from "../components/claims/JobList.jsx";

// The KS view swap: identical list, the attention column replaced by the
// WFM-style editable RDR note (its width and NoteCell come with the key).
const NOTE_COLUMNS = DEFAULT_COLUMNS.map((c) => (c === "attention" ? "note" : c));
import { useMasterCompanyNames } from "../services/useMasterCompanies.js";
import { searchDefaults, searchMoreOpenDefault } from "../services/pageDefaults.js";
import { rowLabel, selectableRows } from "../utils/people.js";
import "../styles/home.css";

// EMPTY_FILTERS = a truly blank search (All / All / …); the "Clear" button
// resets to exactly this. The FIRST-visit landing filters (no saved search)
// come from the user's page defaults instead — searchDefaults() seeds PCO and
// sort (baseline: your own jobs, Needs-Attention sort). "Newest first" and All
// are one flick away, and any in-session change is remembered from then on.
// The accepted sub-statuses, in pipeline order (details.md).
const ACCEPTED_SUBS = ["To Organise", "In Progress", "On Hold"];

// A job a new inspection has sent BACKWARDS: it is at this status now, but it had
// already been further. Labelled by where it sits, because that is what a status
// filter filters on; the Status column then says how far it had got — a job that
// reached Accepted and is back at Requested reads "Accepted (R)".
const RE_OPENED = [
  { code: "R", label: "Re Requested" },
  { code: "I", label: "Re Inspected" },
  { code: "Q", label: "Re Quoted" },
];

const EMPTY_FILTERS = {
  q: "", status: "", accepted_status: "", re_only: "", division_id: "", job_type_id: "",
  project_coordinator_id: "", rule: "", sort: "",
  // ARCHIVE DEFAULTS (Robert 2026-08-21, from using it): every job here is
  // closed history, so the live app's defaults are actively wrong.
  //   * finished "all", not "smart" — Smart select follows the other filters
  //     and lands on "live work only", which in an archive means NOTHING is
  //     returned until you go into Options and say so. Searching job 1000 and
  //     getting an empty page is the first thing this app must never do.
  //   * sort "" (newest first) rather than "attention" — badge weight is
  //     meaningless once the legacy floor has finished the whole estate.
  finished: "all",
  // "more options" row (legacy Search.aspx extras) — collapsed by default
  insurer: "", assessor_company_id: "", assessor_id: "", contractor_company_id: "",
  job_category_id: "", weather_event_id: "", inspector_id: "", after: "", before: "",
};

// The extra filters live behind the More-options expander; when any is active
// the row auto-opens (an invisible active filter silently shrinks results).
const MORE_KEYS = [
  "insurer", "assessor_company_id", "assessor_id", "contractor_company_id",
  "job_category_id", "weather_event_id", "inspector_id", "after", "before",
  // ARCHIVE: "finished" is NOT listed — it is pinned to "all" and has no
  // control, so it can never be a filter somebody set (2026-08-21).
];
// A value equal to the default is not something somebody turned on, so it must
// not count as active — otherwise the expander springs open on every blank page
// and the Clear button permanently reads "(1)". The counters ask: how many
// things are narrowing this BEYOND the normal view.
const isDefault = (k, v) => v === EMPTY_FILTERS[k];
const hasMoreActive = (f) => MORE_KEYS.some((k) => f[k] && !isDefault(k, f[k]));

// How many filters are NARROWING the results right now, for the Clear button.
//
// The failure it exists for (Robert 2026-08-12): you type a job number, press
// Search, and nothing comes back — because a status or a co-ordinator from an
// hour ago is still set, and the filters persist across sessions by design. The
// button said "Clear" whether nothing or six things were on, so there was no
// way to tell at a glance that your results were being narrowed at all.
//
// `q` is excluded: it is visible in the box you just typed into. `sort` too —
// ordering is not narrowing. And the inactive switch WIDENS the dropdowns rather
// than the result set, so it is not a filter either. The count only ever means
// "this many things are hiding jobs from you".
const ACTIVE_FILTER_KEYS = Object.keys(EMPTY_FILTERS).filter((k) => k !== "q" && k !== "sort");
const activeFilterCount = (f) =>
  ACTIVE_FILTER_KEYS.filter((k) => f[k] && !isDefault(k, f[k])).length;

// Optgroup order + labels for the Needs Attention dropdown (mirrors the RDR chip
// order); the engine's group keys are workflow/main/accounts/hs.
const RULE_GROUP_ORDER = [
  ["main", "Main"],
  ["accounts", "Accounts"],
  ["hs", "Health & Safety"],
  ["workflow", "Workflow"],
];

// The "All badges" compact⇄extended toggle is hidden for now (the row-wrap makes
// the full badge set readable, so it isn't needed yet). Its state/handler stay
// wired — flip this to true to bring the control back. Kept as a flag rather than
// deleted so re-enabling is a one-liner, not an archaeology dig.
const SHOW_BADGE_TOGGLE = false;

// The last search survives navigating into a job and back (medispace's
// ProjectsTable pattern). Clear wipes it — for THIS tab only.
// v3: bumped for the needs-attention default sort (Karen 2026-07-16), so every
// browser picks up the new default once. (v2 was the Demo-picks flip.)
// v4: the Finished default became "live work only" and the dropdown values
// changed, so every saved search has to be re-seeded once or the control renders
// blank against a value it no longer offers.
// v5: the default became "smart" and — the part that forces the bump — "live"
// stopped being overridable. Every v4 search has "live" saved on it, which used
// to widen the moment you typed and now would NOT, so without re-seeding, the
// first thing every existing user did (type an address) would silently return
// nothing. A stale saved value is not a cosmetic problem when the same value has
// changed meaning underneath it.
// v6 (Robert 2026-08-20): the remembered search became PER TAB — sessionStorage,
// not localStorage. The office workflow is a phone call mid-job: Ctrl+T, type
// the number, maybe Clear — and none of that may touch the work-queue tab
// sitting next to it. A new tab starts from the user's PAGE DEFAULTS (the
// no-blob path below), refresh and Back/Forward inside a tab keep their state,
// and closing the browser forgets search context. No new-tab detection, no
// heuristics: the storage medium itself is the tab. The medium change is also
// why v6 needs no re-seed protocol — old v5 blobs simply stop being read.
const SEARCH_STATE_KEY = "archiveSearch:last:v1"; // ARCHIVE: own key — a v6 blob holds finished:"smart" + sort:"attention", both of which mean "return nothing useful" here
// The Attention-column swap is a per-USER view preference, not search context
// (same jobs either way — KS 2026-08-19), so it does NOT live in the per-tab
// blob: own localStorage key, the claimSearch:badgesExtended pattern. v5 kept
// it inside the search blob; loadNoteColumn adopts it from there once.

// `keepText` = this mount is a browser BACK/FORWARD rather than a fresh arrival.
function loadSavedSearch(keepText) {
  try {
    const raw = sessionStorage.getItem(SEARCH_STATE_KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw);
    return {
      // The free-text box (Claim #/Client/Address) is dropped on a FRESH entry —
      // a lingering old term silently shrinks the next search and reads as
      // missing data (Robert 2026-07-13). Dropdowns etc. still restore.
      //
      // On BACK it is kept, because Back means "return to the list I was just
      // on": searching a job number, opening it, and having to retype it to
      // reach the next result is the friction Karen Sweatman reported
      // (2026-08-12). The two are told apart by react-router's navigation type
      // — Back/Forward is a POP — so clicking Claim Search in the menu or the
      // logo is a PUSH and still arrives clean, which is exactly the split she
      // asked for. No new persistence machinery: the term is already in the
      // saved blob, it was only ever being discarded on the way back out.
      filters: {
        ...EMPTY_FILTERS,
        ...(saved.filters || {}),
        q: keepText ? saved.filters?.q || "" : "",
      },
      page: Number(saved.page) || 1,
      pageSize: Number(saved.pageSize) || 50,
      showInactive: saved.showInactive === true,
    };
  } catch {
    return null;
  }
}



function saveSearch(filters, page, pageSize, showInactive) {
  try {
    sessionStorage.setItem(
      SEARCH_STATE_KEY,
      JSON.stringify({ filters, page, pageSize, showInactive })
    );
  } catch {
    // ignore storage errors (private mode, etc.)
  }
}

// One fixed-width select for the More-options row. MODULE scope on purpose:
// defined inside the page component it was a new component type every render,
// so React remounted the whole subtree per keystroke (focus loss + wasted work).
function MoreSelect({ name, label, options, value, onChange, width = 170, labelField = "name", narrow }) {
  return (
    <div style={{ width }}>
      <Form.Label className="mb-1 small text-muted">{label}</Form.Label>
      <ThemedSelect size="sm" value={value || ""} onChange={(e) => onChange(name, e.target.value)}>
        <option value="">All</option>
        {narrow(options, value).map((o) => (
          <option key={o.id} value={o.id}>{rowLabel(o, labelField)}</option>
        ))}
      </ThemedSelect>
    </div>
  );
}

// The four Finished values, with the caption that carries the distinction the
// labels cannot: one of them is clever and three are promises.
// OPTIONS — the least-used controls, folded off the filter row (Robert,
// 2026-08-17). Karen Sweatman runs 1080p at 125% text scaling, which is a
// 1536px viewport, and the More-options row wanted ~1930px: it wrapped to a
// third line and cost her a chunk of the job list on every search. Finished and
// Show inactive were the two that wrapped, and that is not a coincidence —
// they are the two nobody touches daily, which is why they ended up last.
//
// Finished belongs here specifically BECAUSE of the smart-select change: the
// default now handles the common cases by itself, so the control exists for the
// occasional deliberate override rather than for daily use.
//
// THE TWO HALVES ARE DELIBERATELY SEPARATED (Robert): Show inactive is not a
// Finished option and must never read as one. Finished decides WHICH JOBS come
// back; Show inactive only decides which NAMES the dropdowns above offer. So
// they get their own headed sections, a divider between them, and the caption
// on each says which of the two questions it answers.
//
// autoClose="outside" so ticking the box or picking a radio does not shut the
// panel — these get changed together, and a menu that closes on every click
// makes setting two things a four-click job.
function SearchOptions({ showInactive, onShowInactive }) {
  // ARCHIVE: the whole FINISHED group is gone (Robert 2026-08-21, second
  // pass): every job here is closed history, so the search is always "all
  // jobs" and a control with one usable value is worse than no control — it
  // invites a click that cannot change anything. `finished` is pinned to
  // "all" in EMPTY_FILTERS and never varies, so it also cannot be the
  // invisible reason for an empty page, which is what this button's
  // highlight existed to warn about.
  const active = showInactive;
  return (
    <Dropdown autoClose="outside">
      <Dropdown.Toggle
        size="sm"
        variant={active ? "outline-primary" : "outline-secondary"}
        id="search-options"
        data-hint="Dropdown contents"
      >
        Options
      </Dropdown.Toggle>
      <Dropdown.Menu style={{ minWidth: 260 }} className="px-3 py-2">
        <div className="small fw-semibold mb-1">Dropdown contents</div>
        <div className="small text-muted mb-2">
          Does not change which jobs are found.
        </div>
        <Form.Check
          type="checkbox"
          id="search-show-inactive"
          className="small"
          label="Show inactive"
          checked={showInactive}
          onChange={(e) => onShowInactive(e.target.checked)}
          data-hint={
            "Adds retired staff and closed list entries to the filter dropdowns on this page " +
            "(co-ordinator, inspector, insurer, assessor, contractor, division, job type, " +
            "category, weather event). It does not change which jobs are found — it only lets " +
            "you pick a name or an entry that is no longer in use, so you can still search for " +
            "older jobs that were logged against one."
          }
        />
      </Dropdown.Menu>
    </Dropdown>
  );
}

function ClaimSearchPage() {
  // Master companies are data now (Owner Data); same name keeps the diff and
  // the saved-search values identical to the old config constant.
  const COMPANY_GROUP_KEYS = useMasterCompanyNames();
  // POP = the browser's Back/Forward; PUSH/REPLACE = a link, the menu, the logo.
  const navigationType = useNavigationType();
  const [saved] = useState(() => loadSavedSearch(navigationType === "POP")); // read once, before first render
  // No saved search → the user's page defaults seed the landing filters.
  const [initialDefaults] = useState(() => searchDefaults(EMPTY_FILTERS));
  const [filters, setFilters] = useState(saved?.filters || initialDefaults);
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(saved?.page || 1);
  const [pageSize, setPageSize] = useState(saved?.pageSize || 50); // 50 default; 500 = "everything"
  const [statuses, setStatuses] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [jobTypes, setJobTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [pcos, setPcos] = useState([]);
  // "More options" expander (1.0's extra dropdowns) — closed unless a saved
  // search still carries one of its filters. Lookups load on first open.
  // One switch for every lookup dropdown on this page (Robert 2026-08-12).
  // Retired staff and retired list rows are hidden by default — a search page
  // must not offer a name nobody can pick any more — but a job logged in 2019
  // may sit on a closed insurer or a departed co-ordinator, and without this
  // there is no way to search for it at all. Remembered with the other filters
  // rather than reset each visit, and counted as a "more" filter below so the
  // panel opens showing it ticked: it can never be quietly on.
  const [showInactive, setShowInactive] = useState(() => saved?.showInactive === true);
  // KS 2026-08-19: the results' attention column can show the WFM-style
  // editable RDR note instead of the badges. A standing view preference,
  // remembered with the search.
  // ARCHIVE: the RDR note IS the column (Robert 2026-08-21) — there are no
  // badges to choose instead of, so it is a constant, not a preference.
  // Open when something inside is ACTIVE (an invisible filter silently shrinks
  // results), or when the user has asked for it to always be open in their page
  // defaults — see searchMoreOpenDefault for why that is a standing preference
  // rather than a first-visit seed.
  const [moreOpen, setMoreOpen] = useState(
    () =>
      hasMoreActive(saved?.filters || EMPTY_FILTERS) ||
      saved?.showInactive === true ||
      searchMoreOpenDefault()
  );
  const [moreLookups, setMoreLookups] = useState(null);
  const [exactMatch, setExactMatch] = useState(false);
  // Smart select resolved to every job rather than live work — reported by the
  // server so the rule lives in ONE place (services/claims.js), not two.
  const [widened, setWidened] = useState(false);
  const [ruleOptions, setRuleOptions] = useState([]);
  const [homeStats, setHomeStats] = useState(null);
  // Extended badge view: every rule chip per row, ON by default (compact =
  // top badge + "+N" — flip the switch off for it).
  const [badgesExtended, setBadgesExtended] = useState(() => {
    try {
      return localStorage.getItem("claimSearch:badgesExtended") !== "0";
    } catch {
      return true;
    }
  });
  const [error, setError] = useState("");
  const navigate = useNavigate();
  // The text that produced the results currently on screen. Enter opens a single
  // hit only while this still matches what is in the box — start typing something
  // new and the shortcut is gone until that search has actually run.
  const [searchedQ, setSearchedQ] = useState(null);
  const [searchParams] = useSearchParams();

  function toggleBadgesExtended(on) {
    setBadgesExtended(on);
    try {
      localStorage.setItem("claimSearch:badgesExtended", on ? "1" : "0");
    } catch {
      /* ignore */
    }
  }

  // LAST ISSUED WINS, not last to arrive. Every search takes a ticket and only
  // the newest one is allowed to write to the screen.
  //
  // Without this the page showed whichever request RESOLVED last, which is not
  // the same thing: press Enter on a slow search and Enter again on a fast one,
  // and the first reply lands on top of the second, so the results on screen are
  // for a search you have already moved on from. Robert hit exactly this shape on
  // 15 Aug — search "priestley", clear the box, Enter, Enter — and the two
  // requests differ wildly in cost (one match versus every claim under the
  // attention sort), which is what makes the race easy to lose.
  //
  // The badge sidecar takes the same ticket. It used to be able to paint one
  // search's chips onto another search's rows, which is worse than being late:
  // the row is right and the chips are lies.
  const searchSeq = useRef(0);

  async function runSearch(active = filters, p = 1, ps = pageSize) {
    setError("");
    const ticket = ++searchSeq.current;
    try {
      const data = await searchClaims(active, { page: p, pageSize: ps });
      if (ticket !== searchSeq.current) return; // a newer search has taken over
      setResults(data.rows);
      setTotal(data.total);
      // The dropdowns still show what they show, so the page has to SAY the
      // search stood them aside — a screen that quietly disagrees with its own
      // results is the fault this feature exists to remove, not a new one.
      setExactMatch(!!data.exact_claim_match && activeFilterCount(active) > 0);
      setWidened(!!data.finished_widened);
      setSearchedQ(active.q);
      setPage(p);
      saveSearch(active, p, ps, showInactive);
      // ARCHIVE: NO BADGE FETCH (2026-08-21). Every job here is below the
      // legacy claim floor, so the rule engine is defined to return nothing —
      // the request could only ever spend a round trip to learn that, and the
      // results show the RDR note rather than badges anyway.
    } catch (err) {
      if (ticket !== searchSeq.current) return; // a newer search owns the screen
      setError(err.response?.data?.error || "Search failed.");
    }
  }

  // Exactly one hit, and it is the hit for what is currently typed.
  const openable = total === 1 && results.length === 1 && filters.q === searchedQ;

  function changePageSize(e) {
    const ps = Number(e.target.value);
    setPageSize(ps);
    runSearch(filters, 1, ps); // page size changed → back to page 1
  }

  useEffect(() => {
    listClaimStatuses().then(setStatuses).catch(() => {});
    listRuleOptions().then(setRuleOptions).catch(() => {});
    getHomeStats().then(setHomeStats).catch(() => {});
    // Deep link (?status=Q — the dashboard's open-book bars): a fresh search on
    // that status beats restoring the remembered one.
    const urlStatus = searchParams.get("status");
    if (urlStatus) {
      // ?status=A&accepted_status=On Hold — the dashboard's Accepted breakdown
      // rows. Without the second half they would all land on "every accepted
      // job", which is the one answer they are trying to narrow down.
      const next = {
        ...EMPTY_FILTERS,
        status: urlStatus,
        accepted_status: searchParams.get("accepted_status") || "",
        re_only: searchParams.get("re_only") || "",
        // ARCHIVE: pinned, never read from the URL — a deep link carrying
        // finished=live would hand somebody an empty page with no visible
        // control to undo it (2026-08-21).
        finished: EMPTY_FILTERS.finished,
      };
      setFilters(next);
      runSearch(next, 1, 50);
      return;
    }
    // Land back on exactly the search the user left. With nothing remembered,
    // the user's page defaults decide the landing filters (baseline: a PCO
    // lands on their own jobs; non-PCOs land on All). Clear stays truly empty.
    if (saved?.filters) {
      runSearch(saved.filters, saved.page || 1, saved.pageSize || 50);
    } else {
      runSearch(initialDefaults, 1, 50);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The dropdown sources that "Show inactive" governs. Split out of the mount
  // effect so flipping the switch reloads exactly these and nothing else.
  //
  // TWO user lists, because this page asks two different questions. The
  // Inspector filter wants INSPECTORS, not every staff account (Karen Sweatman
  // 2026-08-03): `pco: true` was handing it all twenty-odd users — receptionists,
  // accounts, the lot — so the one name you wanted was buried among people who
  // have never inspected anything. That fix then fed the SAME list to the
  // Project Co-Ordinator filter, which is why both dropdowns showed the identical
  // eight names (Karen Sweatman 2026-08-12). The PCO filter wants the accounts
  // flagged shown_in_project_coordinator instead.
  //
  // These are FILTERS, so retired rows are hidden unless asked for. Assignment
  // dropdowns on the job itself always show them; see listUsers in apiClient.js.
  // Fetched WHOLE, once, and narrowed at render (`shown` below). Toggling the
  // switch therefore refetches nothing: no second round trip, no race between a
  // slow reply from the old mode and a fast one from the new, and — the reason
  // that matters — a row that has since been retired still has its NAME here, so
  // a filter left pointing at one can be shown as "Name (inactive)" rather than
  // as a bare id or a silently missing option. These lists are a few dozen rows.
  useEffect(() => {
    let ignore = false;
    const set = (fn) => (rows) => { if (!ignore) fn(rows); };
    listEntityRows("divisions", { includeInactive: true }).then(set(setDivisions)).catch(() => {});
    listEntityRows("job-types", { includeInactive: true }).then(set(setJobTypes)).catch(() => {});
    listUsers({ inspectors: true }).then(set(setUsers)).catch(() => {});
    listUsers({ pco: true }).then(set(setPcos)).catch(() => {});
    return () => { ignore = true; };
  }, []);

  // Lookups for the More-options row load once, on first open.
  useEffect(() => {
    if (!moreOpen || moreLookups) return;
    let ignore = false;
    Promise.all(
      ["insurer-companies", "assessor-companies", "assessors", "contractor-companies",
       "job-categories", "weather-events"].map((k) =>
        listEntityRows(k, { includeInactive: true }).then((rows) => [k, rows]).catch(() => [k, []])
      )
    ).then((pairs) => { if (!ignore) setMoreLookups(Object.fromEntries(pairs)); });
    return () => { ignore = true; };
  }, [moreOpen, moreLookups]);

  function handleFilter(event) {
    const { name, value } = event.target;
    setFilters((current) => ({ ...current, [name]: value }));
  }

  // Toggling only changes what the dropdowns OFFER, so it deliberately does not
  // re-run the search — the current results are still the right answer to the
  // filters as they stand, and re-running would make the switch feel like a
  // filter when it isn't one.
  function toggleShowInactive(on) {
    setShowInactive(on);
    saveSearch(filters, page, pageSize, on);
  }

  // What a lookup dropdown offers. Switch off: active rows, plus whichever row
  // this filter is ALREADY set to even if it has since been retired — otherwise
  // the control renders "All" while the search is still filtered by it, and the
  // screen disagrees with the results. Switch on: everything.
  const shown = (rows, value) => (showInactive ? rows || [] : selectableRows(rows, value));

  // Counted off the LIVE filters, not the ones the last search ran with: if you
  // change a dropdown and haven't pressed Search yet, the number should already
  // reflect what you are about to do.
  const narrowingCount = activeFilterCount(filters);

  // More-options selects/dates auto-search like the main dropdowns.
  function moreFilter(name, value) {
    const next = { ...filters, [name]: value };
    setFilters(next);
    runSearch(next);
  }

  function clearRule() {
    const next = { ...filters, rule: "" };
    setFilters(next);
    runSearch(next);
  }
  // The active rule as a dismissible chip on the filter's label line — one look
  // + one click beats hunting the 50-entry dropdown to see/clear the filter.
  const activeRule = filters.rule ? ruleOptions.find((r) => r.key === filters.rule) : null;

  function clearFilters() {
    try {
      sessionStorage.removeItem(SEARCH_STATE_KEY); // Clear = forget THIS TAB's remembered search
    } catch {
      /* ignore */
    }
    setFilters(EMPTY_FILTERS);
    setPageSize(50);
    // Clear empties the SEARCH, not the user's standing preference: someone
    // whose page default is "Show all options" keeps the row open (KS
    // 2026-09-07); everyone else gets it collapsed, as before.
    setMoreOpen(searchMoreOpenDefault());
    setShowInactive(false); // Clear means a truly blank search, switch included
    runSearch(EMPTY_FILTERS, 1, 50);
  }

  return (
    <div>
        {/* Numbering simply continues on from the imported live jobs (go-live plan),
            so the old "change next number" control is gone — display only. */}
        <div className="d-flex flex-wrap justify-content-between align-items-center column-gap-4 row-gap-2 mb-3">
          {/* The app header already titles this page — no duplicate h4 (real estate). */}
          <div className="d-flex align-items-center column-gap-4 flex-wrap">
            {/* Fixed-width lead so the RDR chips start at the same anchor as on
                the report pages — no jumping when switching between them. */}
            {/* Sparkline retired from this band (Robert 2026-07-13) — it lives
                on the Dashboard now, which is also where login lands. */}
            <div className="rdr-chip-lead d-flex align-items-center column-gap-3">
              <HomeStatsInline stats={homeStats} />
              {homeStats?.anchor ? <div className="vr" /> : null}
            </div>
            {/* Karen Renall's system view: the familiar RDR groups, placeholder
                pages for now — counts arrive with the next-action rule engine. */}
            <RdrChips active="search" />
          </div>
        </div>

        {/* "You handled X recently" — self-contained; renders nothing when empty. */}
        <RecentCompletions />

        {error ? <div className="alert alert-danger">{error}</div> : null}

        {/* Claims-logged-by-day chart removed from here (2026-07-04, Robert —
            may come back); it lives on /rdr/main, admin anchor control included. */}

        {/* ONE line at 1080p: the search box grows, selects are fixed-width,
            buttons + the demo switch ride the end (TabHeader one-line rule). */}
        <div className="d-flex align-items-end column-gap-2 row-gap-2 flex-wrap mb-3">
          {/* 20px off both ends (Robert 2026-08-17) — what people type here is a
              job number or a street name, which fit in far less than the label
              above needs, so it was the cheapest 20px on the row. */}
          <div className="flex-grow-1" style={{ minWidth: 230, maxWidth: 270 }}>
            {/* Also matches phone #s, insurance claim # and assessor ref. */}
            {/* The label carries the hint, and only while the shortcut is live —
                a shortcut nobody is told about is a shortcut nobody uses, and one
                advertised when it will not work is worse than none.
                text-TRUNCATE, not text-nowrap: this label is the widest thing on
                the row and it is theme-dependent — measured 244px in Cosmo and
                282px in Lux, against a 270px box. Under nowrap the long themes
                simply painted it OVER the Status label next door; truncating
                puts an ellipsis inside its own box instead, and the title keeps
                the full list one hover away. Sizing the box to the widest theme
                would have meant giving the 20px back to the one theme nobody
                here uses. */}
            <Form.Label
              className="mb-1 small text-muted text-truncate d-block"
              data-hint="Search by job number, insurance claim number, client, address, phone or loss-adjuster reference"
            >
              {openable
                ? <span className="text-success fw-semibold">One match — press Enter again to open it</span>
                : "Job # / Claim # / Client / Address / Ph / LA Ref"}
            </Form.Label>
            <Form.Control
              size="sm"
              name="q"
              value={filters.q}
              onChange={handleFilter}
              onKeyDown={(e) => {
                if (e.key !== "Enter") return;
                // Paste a job number, Enter to search, Enter again to open it
                // (Robert 2026-08-02). Only when the search has already run and
                // found exactly ONE claim — never a guess at which of several was
                // meant, and never before the results on screen belong to what is
                // in the box.
                if (openable) navigate(`/claims/${results[0].claim_number}`);
                else runSearch();
              }}
            />
          </div>
          {/* Status / Division / Job Type auto-search on select (Karen 2026-07-13)
              — same reasoning as PM: picking one is a whole new working set. The
              free-text box stays enter-to-search. */}
          <div style={{ width: 170 }}>
            <Form.Label className="mb-1 small text-muted">Status</Form.Label>
            <ThemedSelect
              size="sm" name="status"
              value={
                filters.re_only ? `re:${filters.status}`
                : filters.accepted_status ? `A:${filters.accepted_status}`
                : filters.status
              }
              onChange={(e) => {
                const [head, tail = ""] = e.target.value.split(":");
                // Three encodings in one <select>: a bare code, "A:<sub>" for the
                // accepted sub-statuses, and "re:<code>" for the re-opened ones.
                const next =
                  head === "re"
                    ? { ...filters, status: tail, accepted_status: "", re_only: "1" }
                    : { ...filters, status: head, accepted_status: tail, re_only: "" };
                setFilters(next);
                runSearch(next);
              }}
            >
              <option value="">All</option>
              {statuses.map((s) => (
                <option key={s.code} value={s.code}>{s.name}</option>
              ))}
              {/* 1.0 carries these three in the same dropdown (Accepted To
                  Organise / In Progress / On Hold). One <select> can only emit
                  one value, so the sub-status ones use "A:<sub>" and are split
                  on change — the URL still carries the two as separate params,
                  which is what the dashboard's Accepted rows link to. */}
              {/* A blank row sets the Accepted sub-statuses apart from the plain
                  ones (Robert 2026-08-05). Tried an <optgroup> first: it groups
                  correctly but the browser draws the label bold with the items
                  indented, and none of that is styleable, so it did not sit with
                  the rest of the page. A blank disabled row is the quiet version. */}
              <option disabled />
              {ACCEPTED_SUBS.map((sub) => (
                <option key={sub} value={`A:${sub}`}>Accepted {sub}</option>
              ))}
              {/* Jobs a new inspection has RE-OPENED: at this status now, but
                  they had already gone further (Robert, 13 Aug). Their own group
                  behind a second blank row, same quiet separator as above. The
                  Status column names how far each one got — "Accepted (R)". */}
              <option disabled />
              {RE_OPENED.map((r) => (
                <option key={r.code} value={`re:${r.code}`}>{r.label}</option>
              ))}
            </ThemedSelect>
          </div>
          <div style={{ width: 170 }}>
            <Form.Label className="mb-1 small text-muted">Division</Form.Label>
            <ThemedSelect
              size="sm" name="division_id" value={filters.division_id}
              onChange={(e) => {
                const next = { ...filters, division_id: e.target.value };
                setFilters(next);
                runSearch(next);
              }}
            >
              <option value="">All</option>
              {shown(divisions, filters.division_id).map((d) => (
                <option key={d.id} value={d.id}>{rowLabel(d, "name")}</option>
              ))}
            </ThemedSelect>
          </div>
          <div style={{ width: 200 }}>
            <Form.Label className="mb-1 small text-muted">Job Type</Form.Label>
            <ThemedSelect
              size="sm" name="job_type_id" value={filters.job_type_id}
              onChange={(e) => {
                const next = { ...filters, job_type_id: e.target.value };
                setFilters(next);
                runSearch(next);
              }}
            >
              <option value="">All</option>
              {shown(jobTypes, filters.job_type_id).map((t) => (
                <option key={t.id} value={t.id}>{rowLabel(t, "name")}</option>
              ))}
            </ThemedSelect>
          </div>
          <div style={{ width: 200 }}>
            <Form.Label className="mb-1 small text-muted">Project Co-Ordinator</Form.Label>
            {/* PM change auto-searches (like Sort) — picking a manager is a
                whole new working set, not a refinement you tweak then submit. */}
            <ThemedSelect
              size="sm" name="project_coordinator_id" value={filters.project_coordinator_id}
              onChange={(e) => {
                const next = { ...filters, project_coordinator_id: e.target.value };
                setFilters(next);
                runSearch(next);
              }}
            >
              <option value="">All</option>
              {shown(pcos, filters.project_coordinator_id).map((u) => (
                <option key={u.id} value={u.id}>{rowLabel(u)}</option>
              ))}
            </ThemedSelect>
          </div>
          {/* Needs Attention filter: pick a badge → only jobs wearing it (the PM's
              "let me work all the To-Organise-Overdue jobs" move). Auto-searches.
              While active, the rule shows as a dismissible chip in the JobList's
              Needs Attention COLUMN HEADER (room to breathe there — the filter
              label line is too cramped for a full rule name). */}
          <div style={{ width: 220 }}>
            <Form.Label className="mb-1 small text-muted">Needs Attention</Form.Label>
            <ThemedSelect
              size="sm" name="rule" value={filters.rule || ""}
              onChange={(e) => {
                const next = { ...filters, rule: e.target.value };
                setFilters(next);
                runSearch(next);
              }}
              data-hint="Show only jobs carrying this needs-attention badge"
            >
              <option value="">All</option>
              {RULE_GROUP_ORDER.map(([g, groupLabel]) => {
                const opts = ruleOptions.filter((r) => r.group === g);
                return opts.length ? (
                  <optgroup key={g} label={groupLabel}>
                    {opts.map((r) => (
                      <option key={r.key} value={r.key}>{r.label}</option>
                    ))}
                  </optgroup>
                ) : null;
              })}
            </ThemedSelect>
          </div>
          <Button size="sm" onClick={() => runSearch()}>Search</Button>
          {/* The count is the whole point: a filter you cannot see is a filter
              you cannot blame for an empty result. */}
          <Button
            size="sm"
            variant="secondary"
            onClick={clearFilters}
            data-hint={
              narrowingCount === 0
                ? "Reset the search to blank"
                : `${narrowingCount} ${narrowingCount === 1 ? "filter is" : "filters are"} narrowing these results — clear resets everything`
            }
          >
            {narrowingCount > 0 ? `Clear (${narrowingCount})` : "Clear"}
          </Button>
          {/* 1.0's extra dropdowns live behind this expander — NOT default
              visible (Robert). Auto-opens when a remembered search uses one. */}
          <Button
            size="sm"
            variant={moreOpen || hasMoreActive(filters) ? "outline-primary" : "outline-secondary"}
            onClick={() => setMoreOpen((o) => !o)}
          >
            {moreOpen ? "Less options" : "More options"}
          </Button>
          {SHOW_BADGE_TOGGLE ? (
            <Form.Check
              type="switch"
              id="badges-extended"
              className="ms-1 text-nowrap"
              style={{ marginBottom: 3 }}
              label="All badges"
              checked={badgesExtended}
              onChange={(e) => toggleBadgesExtended(e.target.checked)}
              data-hint="Off = each job's top badge only · on = every needs-attention badge"
            />
          ) : null}
        </div>

        {moreOpen ? (
          <div className="d-flex align-items-end column-gap-2 row-gap-2 flex-wrap mb-3">
            {/* Insurer leads with the master groups (VERO ⇒ every Vero/AMP
                company — legacy ParentCompanies). */}
            <div style={{ width: 195 }}>
              <Form.Label className="mb-1 small text-muted">Insurer</Form.Label>
              <ThemedSelect
                size="sm"
                value={filters.insurer || ""}
                onChange={(e) => moreFilter("insurer", e.target.value)}
              >
                <option value="">All</option>
                {COMPANY_GROUP_KEYS.map((g) => (
                  <option key={g} value={g}>{g} (group)</option>
                ))}
                {shown(moreLookups?.["insurer-companies"], filters.insurer).map((o) => (
                  <option key={o.id} value={o.id}>{rowLabel(o, "name")}</option>
                ))}
              </ThemedSelect>
            </div>
            <MoreSelect name="assessor_id" label="Assessor" options={moreLookups?.assessors} value={filters.assessor_id} onChange={moreFilter} narrow={shown} width={185} />
            <MoreSelect name="assessor_company_id" label="Assessor Company" options={moreLookups?.["assessor-companies"]} value={filters.assessor_company_id} onChange={moreFilter} narrow={shown} width={190} />
            {/* Contractor = jobs with an ORDER for them (legacy relationship search). */}
            <MoreSelect name="contractor_company_id" label="Contractor" options={moreLookups?.["contractor-companies"]} value={filters.contractor_company_id} onChange={moreFilter} narrow={shown} width={190} />
            <MoreSelect name="job_category_id" label="Category" options={moreLookups?.["job-categories"]} value={filters.job_category_id} onChange={moreFilter} narrow={shown} width={155} />
            <MoreSelect name="weather_event_id" label="Weather Event" options={moreLookups?.["weather-events"]} value={filters.weather_event_id} onChange={moreFilter} narrow={shown} width={175} />
            <MoreSelect name="inspector_id" label="Inspector" options={users} value={filters.inspector_id} onChange={moreFilter} narrow={shown} width={165} labelField="full_name" />
            {/* Dates apply to the SELECTED status's date (Quoted date when
                Status=Quoted…), or the logged date with no status — as 1.0.
                130 measured, not guessed: a dd/mm/yyyy field plus its calendar
                button needs ~122px in the widest theme, so this is the text
                plus a hair (Robert 2026-08-17: "we can reduce the size of after
                and before by a bit"). */}
            <div style={{ width: 130 }}>
              <Form.Label className="mb-1 small text-muted" data-hint="On/after this date — applies to the selected status's date (logged date if none)">
                After
              </Form.Label>
              <Form.Control
                size="sm" type="date" value={filters.after || ""}
                onChange={(e) => moreFilter("after", e.target.value)}
              />
            </div>
            <div style={{ width: 130 }}>
              <Form.Label className="mb-1 small text-muted" data-hint="On/before this date — applies to the selected status's date (logged date if none)">
                Before
              </Form.Label>
              <Form.Control
                size="sm" type="date" value={filters.before || ""}
                onChange={(e) => moreFilter("before", e.target.value)}
              />
            </div>
            {/* Last in the row on purpose: it governs the dropdowns above it.
                Finished and Show inactive live INSIDE it — see SearchOptions for
                why those two and not others, and why they are kept visibly
                apart. Bottom-aligned with the selects rather than the labels,
                which is what the wrapper div is for. */}
            <div>
              <SearchOptions
                showInactive={showInactive}
                onShowInactive={toggleShowInactive}
              />
            </div>
          </div>
        ) : null}

        {/* ARCHIVE: the RDR note is the column, always — and no badges are
            passed, because every job is below the legacy floor and the engine
            is defined to return none. */}
        <JobList
          rows={results}
          sortKey={filters.sort || ""}
          onSort={(next) => {
            const updated = { ...filters, sort: next };
            setFilters(updated);
            runSearch(updated);
          }}
          columns={NOTE_COLUMNS}
          activeFilter={activeRule}
          onClearFilter={clearRule}
        />

        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-2">
          <span className="small text-muted">
            {total === 0
              ? "No claims"
              : `Showing ${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)} of ${total}`}
            {total > 1000 ? <span className="text-warning ms-1">(capped at 1000 — narrow with filters)</span> : null}
            {/* SMART SELECT went wide, so say what it decided. This used to be
                an apology — the dropdown said "live work only" and the search
                had overridden it — and now it is simply the control reporting
                its own reasoning, which is the job a vague label hands it. It
                stays because "did this include old jobs?" is a real question
                whichever way the answer was arrived at. Never appears for an
                explicit choice: those are absolute and have nothing to report.
                Same shape as the exact-job-number note below. */}
                        {/* Only when filters were actually set aside — saying "filters
                ignored" when none were on would be noise, and there is nothing
                to explain. */}
            {exactMatch ? (
              <span className="ms-1" data-hint="An exact job number goes straight to the job, so the dropdowns don't apply">
                — exact job number, other filters ignored
              </span>
            ) : null}
          </span>
          <div className="d-flex align-items-center gap-2">
            <ThemedSelect size="sm" style={{ width: 150 }} value={pageSize} onChange={changePageSize}>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
              <option value={500}>500 per page</option>
              <option value={1000}>All (max 1000)</option>
            </ThemedSelect>
            <Button size="sm" variant="outline-secondary" disabled={page <= 1} onClick={() => runSearch(filters, page - 1)}>
              ‹ Prev
            </Button>
            <span className="small text-muted">
              Page {page} of {Math.max(1, Math.ceil(total / pageSize))}
            </span>
            <Button size="sm" variant="outline-secondary" disabled={page >= Math.ceil(total / pageSize)} onClick={() => runSearch(filters, page + 1)}>
              Next ›
            </Button>
          </div>
        </div>
    </div>
  );
}

export default ClaimSearchPage;
